import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  message, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Statistic,
  Tag,
  DatePicker,
  Select
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PlusOutlined,
  MinusOutlined,
  UpOutlined,
  SyncOutlined,
  FileTextOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import dayjs from 'dayjs';
import formatCurrency from '../../utils/formatCurrency';
import { responsiveFilterSelectProps } from '../../utils/responsiveFilterSelect';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AccountTransactions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    dateRange: null
  });

  const fetchAccountInfo = useCallback(async () => {
    try {
      const response = await axios.get('/client/balance');
      setAccountInfo(response.data);
    } catch (error) {
      console.error('Error fetching account info:', error);
      message.error('Failed to load account information');
    }
  }, []);

  const fetchTransactions = useCallback(async (page = 1, pageSize = 10, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...(currentFilters.type && { type: currentFilters.type }),
        ...(currentFilters.dateRange && {
          startDate: currentFilters.dateRange[0].format('YYYY-MM-DD'),
          endDate: currentFilters.dateRange[1].format('YYYY-MM-DD')
        })
      };

      const response = await axios.get('/client/transactions', { params });

      const list = response.data.transactions || [];
      const responsePagination = response.data.pagination || {};
      const total = typeof response.data.total === 'number'
        ? response.data.total
        : (responsePagination.total || list.length);

      setTransactions(list);
      setPagination({
        current: responsePagination.current || page,
        pageSize: responsePagination.pageSize || pageSize,
        total
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAccountInfo();
  }, [fetchAccountInfo]);

  useEffect(() => {
    fetchTransactions(1, pagination.pageSize, filters);
  }, [fetchTransactions, filters, pagination.pageSize]);

  const handleTableChange = (pagination) => {
    fetchTransactions(pagination.current, pagination.pageSize);
  };

  const handleFilterChange = (key, value) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setFilters(prev => ({
      ...prev,
      [key]: value || (key === 'type' ? '' : null)
    }));
  };

  const resetFilters = () => {
    setFilters({ type: '', dateRange: null });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Use global formatCurrency utility
  // Removed local impl, use utils/formatCurrency


  const getTransactionTypeDisplay = (type) => {
    const types = {
      'consume': { color: 'red', icon: <MinusOutlined />, label: 'Purchase' },
      'topup': { color: 'green', icon: <PlusOutlined />, label: 'Top-up' },
      'repay': { color: 'blue', icon: <UpOutlined />, label: 'Repayment' },
      'refund': { color: 'cyan', icon: <SyncOutlined />, label: 'Refund' },
      'adjustment': { color: 'orange', icon: <FileTextOutlined />, label: 'Adjustment' }
    };
    return types[type] || { color: 'default', icon: <FileTextOutlined />, label: type };
  };

  const getBalanceChangeDisplay = (transaction) => {
    const { type, amount } = transaction;
    // const typeInfo = getTransactionTypeDisplay(type); // Removed unused var
    
    if (type === 'consume') {
      return (
        <Text type="danger" style={{ fontWeight: 'bold' }}>
          -{formatCurrency(amount, accountInfo?.currency)}
        </Text>
      );
    } else {
      return (
        <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
          +{formatCurrency(amount, accountInfo?.currency)}
        </Text>
      );
    }
  };

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => (
        <div>
          <div>{dayjs(date).format('MMM DD, YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(date).format('HH:mm:ss')}
          </Text>
        </div>
      ),
      sorter: true
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const typeInfo = getTransactionTypeDisplay(type);
        return (
          <Tag color={typeInfo.color} icon={typeInfo.icon}>
            {typeInfo.label}
          </Tag>
        );
      }
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount, record) => getBalanceChangeDisplay(record),
      align: 'right'
    },
    {
      title: 'Balance Snapshot',
      key: 'balanceSnapshot',
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>
            <Text type="secondary">Top-up: </Text>
            <Text style={{ color: '#52c41a' }}>
              {formatCurrency(record.balanceSnapshot?.topUpBalance || 0, accountInfo?.currency)}
            </Text>
          </div>
          <div>
            <Text type="secondary">Credit: </Text>
            <Text style={{ color: '#fa8c16' }}>
              {formatCurrency(record.balanceSnapshot?.creditUsed || 0, accountInfo?.currency)}
            </Text>
          </div>
          <div>
            <Text type="secondary">Total: </Text>
            <Text style={{ fontWeight: 'bold' }}>
              {formatCurrency(record.balanceSnapshot?.totalAvailableBalance || 0, accountInfo?.currency)}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Description',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.description || 'No description'}
          </div>
          {notes && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {notes}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const safeStatus = status || 'unknown';
        return (
          <Tag color={safeStatus === 'completed' ? 'green' : safeStatus === 'pending' ? 'orange' : 'red'}>
            {safeStatus.toUpperCase()}
          </Tag>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card 
        style={{ marginBottom: '16px' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/client/dashboard')}
            >
              Back to Dashboard
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Transaction History
            </Title>
          </Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchTransactions(pagination.current, pagination.pageSize, filters)}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Account Summary */}
      {accountInfo && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total Available"
                value={accountInfo.totalAvailableBalance || 0}
                precision={2}
                valueStyle={{ color: '#722ed1', fontSize: '22px', fontWeight: 'bold' }}
                prefix="$"
              />
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                <span style={{ marginRight: 12 }}>Top-up: {formatCurrency(accountInfo.topUpBalance, accountInfo.currency)}</span>
                <span style={{ marginRight: 12 }}>Credit Used: {formatCurrency(accountInfo.creditUsed, accountInfo.currency)}</span>
                <span>Credit Limit: {formatCurrency(accountInfo.creditLimit, accountInfo.currency)}</span>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Top-up Balance"
                value={accountInfo.topUpBalance || 0}
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                prefix="$"
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Credit Used"
                value={accountInfo.creditUsed || 0}
                precision={2}
                valueStyle={{ color: '#fa8c16' }}
                prefix="$"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Filters */}
      <Card className="responsive-filter-card" style={{ marginBottom: 16 }}>
        <Row className="responsive-filter-row" gutter={[16, 12]} align="middle">
          <Col xs={24} sm={8}>
            <div className="responsive-filter-field" data-filter-label="Type">
              <Select
                placeholder="All Types"
                allowClear
                value={filters.type || undefined}
                onChange={(value) => handleFilterChange('type', value)}
                {...responsiveFilterSelectProps}
              >
                <Option value="consume">Purchase</Option>
                <Option value="topup">Top-up</Option>
                <Option value="repay">Repayment</Option>
                <Option value="refund">Refund</Option>
                <Option value="adjustment">Adjustment</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="responsive-filter-field" data-filter-label="Date Range">
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="responsive-filter-actions" style={{ paddingTop: 18 }}>
              <Button onClick={resetFilters}>
                Reset Filters
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="_id"
          loading={loading}
          pagination={{
            total: pagination.total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} transactions`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default AccountTransactions;
